import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const BulkActionsBar = ({ selectedCredits, onBulkAction, onClearSelection }) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const actionOptions = [
    { value: '', label: 'Select action...' },
    { value: 'transfer', label: 'Transfer Credits' },
    { value: 'retire', label: 'Retire Credits' },
    { value: 'create-claim', label: 'Create VCMI Claim' },
    { value: 'generate-dossier', label: 'Generate Dossier' },
    { value: 'export', label: 'Export Data' },
    { value: 'tag', label: 'Add Tags' }
  ];

  const handleExecuteAction = async () => {
    if (!selectedAction || selectedCredits?.length === 0) return;

    setIsProcessing(true);
    try {
      await onBulkAction(selectedAction, selectedCredits);
      setSelectedAction('');
    } catch (error) {
      console.error('Bulk action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'transfer': return 'ArrowRightLeft';
      case 'retire': return 'Archive';
      case 'create-claim': return 'FileText';
      case 'generate-dossier': return 'FolderOpen';
      case 'export': return 'Download';
      case 'tag': return 'Tag';
      default: return 'Play';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'retire': return 'warning';
      case 'create-claim': return 'success';
      case 'transfer': return 'default';
      default: return 'default';
    }
  };

  if (selectedCredits?.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
      <div className="bg-card border border-border rounded-lg shadow-lg p-4 min-w-96">
        <div className="flex items-center justify-between space-x-4">
          {/* Selection Info */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-full">
              <span className="text-sm font-medium text-primary-foreground">
                {selectedCredits?.length}
              </span>
            </div>
            <div>
              <div className="text-sm font-medium text-card-foreground">
                {selectedCredits?.length} credit{selectedCredits?.length !== 1 ? 's' : ''} selected
              </div>
              <div className="text-xs text-muted-foreground">
                Choose an action to perform
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-3">
            <Select
              options={actionOptions}
              value={selectedAction}
              onChange={setSelectedAction}
              placeholder="Select action..."
              className="min-w-48"
            />
            
            <Button
              variant={getActionColor(selectedAction)}
              iconName={getActionIcon(selectedAction)}
              onClick={handleExecuteAction}
              disabled={!selectedAction || isProcessing}
              loading={isProcessing}
            >
              Execute
            </Button>
            
            <Button
              variant="ghost"
              iconName="X"
              onClick={onClearSelection}
              disabled={isProcessing}
            >
              Clear
            </Button>
          </div>
        </div>

        {/* Action Preview */}
        {selectedAction && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="text-xs text-muted-foreground">
              {selectedAction === 'transfer' && 'Credits will be prepared for transfer to another registry account'}
              {selectedAction === 'retire' && 'Credits will be permanently retired and cannot be used again'}
              {selectedAction === 'create-claim' && 'VCMI-compliant claims will be generated for selected credits'}
              {selectedAction === 'generate-dossier' && 'Comprehensive documentation packages will be created'}
              {selectedAction === 'export' && 'Credit data will be exported in CSV format'}
              {selectedAction === 'tag' && 'Custom tags will be added to help organize credits'}
            </div>
          </div>
        )}

        {/* Validation Warnings */}
        {selectedAction === 'retire' && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center space-x-2 text-warning">
              <Icon name="AlertTriangle" size={16} />
              <span className="text-xs">Warning: Retirement is permanent and cannot be undone</span>
            </div>
          </div>
        )}

        {selectedAction === 'create-claim' && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center space-x-2 text-primary">
              <Icon name="Info" size={16} />
              <span className="text-xs">Claims will be validated against VCMI requirements</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkActionsBar;